import {
  faBars,
  faRefresh,
  faSearch,
  faSpinner,
  faUserCircle,
  faXmark
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useContext, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import DialpadButton from '@/components/DialpadButton';
import HidePhiButton from '@/components/HidePhiButton';
import NarrowNavDrawer from '@/components/NarrowNavDrawer';
import ProfileMenu from '@/components/ProfileMenu';
import { STICKY_CHROME_CLASS } from '@/constants/layout';
import AppContext from '@/contexts/appContext';
import { useNarrowNav } from '@/contexts/narrowNavContext';
import { Avatar, Breadcrumbs, type BreadcrumbItem } from '@/ui';
import resolveCurrentNavLabel from '@/utils/resolveCurrentNavLabel';
import { getAvailableWorkspaceNavOptions } from '@/utils/workspaceNavOptions';

import { Props } from './types';

const ICON_BUTTON = 'shrink-0 rounded-md px-1.5 py-1 hover:text-gray-700';

/**
 * Dugout's top bar.
 *
 * On the dropdown navigation it is the narrow layout's bar only, and hides above `lg` where the
 * desktop sidebar carries these actions.
 *
 * On the menu navigation it stays up at every width, so Dugout reads the same in the extension
 * panel and in a full window: the menu button and the page you are in on the leading edge, the
 * account menu top right. The page you are in reads as a breadcrumb — the workspace entry, then the
 * section page that owns the route (`People / Profile`) — so where you are holds still in one spot
 * instead of hiding behind the menu, and the first crumb climbs back to that entry's list. On a
 * narrow view the button opens the menu, which flies out under the
 * bar so the button stays put and turns into its close. On a wide view the rails are the
 * navigation, so the button drops out. It goes on the leading edge so the trailing cluster —
 * search, the dialer, the account menu — stays put for anyone who reaches for it without looking.
 *
 * It is pinned to the top of the viewport, like the person band under it: the menu button is the
 * only way into the navigation on a narrow view, so scrolling must not carry it off the page. Its
 * height is fixed at the 2rem the bands below it are pinned to, so the chrome stacks flush
 * however few controls the bar is left holding.
 */
const GlobalNavHeader = ({ currentAction, resetActiveTab, onOpenGlobalSearch }: Props) => {
  const { pathname } = useLocation();
  const { currentUser, pendingRequests, permissions } = useContext(AppContext);
  const { navStyle, menuOpen, setMenuOpen, wide, section } = useNarrowNav();
  const isMenu = navStyle === 'drawer';

  const workspaceItems = useMemo(() => getAvailableWorkspaceNavOptions(permissions), [permissions]);
  const currentWorkspaceLabel = resolveCurrentNavLabel(workspaceItems, pathname);
  const currentWorkspaceItem = workspaceItems.find((item) => item.label === currentWorkspaceLabel);

  /*
   * The section's own name stays out: on a person it is their name, and the bar is on screen
   * whoever is looking over a shoulder. The page within the section is what was getting lost.
   */
  const breadcrumbs: BreadcrumbItem[] = [
    ...(currentWorkspaceItem
      ? [{ label: currentWorkspaceItem.label, to: currentWorkspaceItem.path }]
      : []),
    ...(section?.currentLabel ? [{ label: section.currentLabel }] : [])
  ];

  const userName = currentUser
    ? `${currentUser.first_name} ${currentUser.last_name}`.trim()
    : 'My Profile';

  return (
    <nav
      data-sticky-chrome
      className={`flex h-8 shrink-0 justify-between items-center px-2 text-xs z-21 ${
        isMenu
          ? 'bg-white border-b border-gray-200 text-gray-700'
          : 'bg-gray-100 text-gray-500 lg:hidden'
      } ${STICKY_CHROME_CLASS.topBar}`}>
      {isMenu && (
        <button
          type="button"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label={menuOpen ? 'Close navigation menu' : 'Open navigation menu'}
          aria-expanded={menuOpen}
          className="mr-2 w-8 shrink-0 rounded-md py-1 hover:text-gray-700 lg:hidden">
          <FontAwesomeIcon icon={menuOpen ? faXmark : faBars} className="fa-lg" />
        </button>
      )}

      {isMenu ? (
        <Breadcrumbs items={breadcrumbs} className="grow" />
      ) : (
        <div className="flex justify-around grow">
          <button type="button" onClick={resetActiveTab}>
            ACTIVE TAB RESET
            <FontAwesomeIcon className="ml-1" icon={faRefresh} />
          </button>
        </div>
      )}

      <div className="flex items-center gap-2">
        {pendingRequests.length > 0 && (
          <div
            className="flex items-center mr-2"
            title="Pending requests"
            aria-label="Pending requests">
            <FontAwesomeIcon className="animate-spin fa-lg" icon={faSpinner} />
          </div>
        )}

        {/* The menu carries the labelled reset in its footer; the bar keeps a one-tap copy. */}
        {isMenu && (
          <button
            type="button"
            onClick={resetActiveTab}
            aria-label="Reset active tab"
            title="Reset active tab"
            className={ICON_BUTTON}>
            <FontAwesomeIcon icon={faRefresh} />
          </button>
        )}

        <HidePhiButton />

        <button
          type="button"
          onClick={onOpenGlobalSearch}
          aria-label="Search"
          className={isMenu ? ICON_BUTTON : 'hover:text-gray-700'}>
          <FontAwesomeIcon icon={faSearch} />
        </button>

        <DialpadButton className={isMenu ? ICON_BUTTON : undefined} />

        <ProfileMenu
          originUrl={currentAction?.origin}
          buttonAriaLabel={isMenu ? `Account menu for ${userName}` : undefined}
          buttonText={
            isMenu ? (
              <Avatar src={currentUser?.avatar_url} name={userName} size="sm" />
            ) : (
              <FontAwesomeIcon icon={faUserCircle} className="fa-2x" />
            )
          }
          customButtonClassName="hover:text-gray-700"
          // Above the menu flying out under the bar, which shares the dropdown's z-30.
          customMenuClassName={isMenu ? 'right-0 mt-1 origin-top-right z-40!' : undefined}
        />
      </div>

      {isMenu && !wide && (
        <NarrowNavDrawer
          open={menuOpen}
          onClose={() => setMenuOpen(false)}
          resetActiveTab={resetActiveTab}
        />
      )}
    </nav>
  );
};

export default GlobalNavHeader;
